import {defineStore} from "pinia";
import useCrudStore from "@/store/composables/crud";
import environment from "@/environment";
import httpClient from "@/tools/http-client";
import {ref} from "vue";
import {useSettingsStore} from "@/store/settings";

export const DEFAULT_ORDERS_PER_PAGE = 5;

function createIdempotencyKey() {
    const browserCrypto = window.crypto;
    if (browserCrypto?.randomUUID) {
        return browserCrypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (browserCrypto?.getRandomValues) {
        browserCrypto.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index++) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const useOrdersStore
    = defineStore('OrdersStore', () => {
    const ordersEndpoint = environment.appConfig.baseApiUrl + "orders";
    const makeOrderEndpoint = ordersEndpoint + '/make';
    const promoValidateEndpoint = environment.appConfig.baseApiUrl + "promo-codes/validate";
    const checkPaymentEndpoint = environment.appConfig.baseApiUrl  + 'check_payment';

    const crud = useCrudStore(ordersEndpoint);
    let checkoutIdempotencyKey = null;
    let promoValidationRequestId = 0;

    const checkoutData = ref({
        phone: '',
        fio: '',
        email: '',
    });

    // Применённый промокод и рассчитанная сервером скидка.
    const promo = ref(null);

    async function applyPromo(code, ids) {
        const requestId = ++promoValidationRequestId;
        crud.startLoading();
        try {
            const resp = await httpClient.post(promoValidateEndpoint, {code, ids});
            if (requestId !== promoValidationRequestId) {
                return {stale: true};
            }
            if (resp.data.status === 'error') {
                crud.setError(resp.data.message);
                return null;
            }
            crud.clearLoading();
            promo.value = resp.data.promo_code || null;
            return promo.value;
        } catch (e) {
            if (requestId !== promoValidationRequestId) {
                return {stale: true};
            }
            crud.setError(e);
            return null;
        }
    }

    function clearPromo() {
        promoValidationRequestId++;
        promo.value = null;
    }

    async function makeOrder({ids, delivery_point, comment, email, phone, fio, use_delivery, promo_code }) {
        crud.startLoading();
        try {
            checkoutIdempotencyKey ??= createIdempotencyKey();
            const resp
                = await httpClient.post(
                    makeOrderEndpoint,
                    {ids, delivery_point, comment, email, phone, fio, use_delivery, promo_code},
                    {headers: {'Idempotency-Key': checkoutIdempotencyKey}},
                );
            if (resp.data.status === 'error') {
                crud.setError(resp.data.message);
                return
            }
            crud.setRecord(resp.data.order);
            checkoutIdempotencyKey = null;
            clearPromo();
            crud.clearLoading();
        }catch (e) {
            crud.setError(e);
        }
    }
    async function checkPayment(orderId) {
        crud.startLoading();
        try {
            const resp
                = await httpClient.post(checkPaymentEndpoint, {order_id: orderId});
            return resp.data.message;
        }catch (e) {
            crud.setError(e);
        }
    }
    async function cancelOrder(orderId) {
        crud.startLoading();
        try {
            const resp = await httpClient.post(`${ordersEndpoint}/${encodeURIComponent(orderId)}/cancel`);
            if (resp.data.status === 'error') {
                crud.setError(resp.data.message);
                return false;
            }
            crud.removeRecord(orderId);
            const settingsStore = useSettingsStore();
            settingsStore.orderCount = Math.max(0, settingsStore.orderCount - 1);
            crud.clearLoading();
            return true;
        } catch (e) {
            crud.setError(e);
            return false;
        }
    }
    async function loadData(metaParams) {
        console.log("orders: loadData");
        crud.startLoading();
        try {
            metaParams.page = Number(metaParams.page || 0) + 1;
            metaParams.per_page = Number(metaParams.per_page || DEFAULT_ORDERS_PER_PAGE);

            const resp = await httpClient.get(ordersEndpoint, {params: metaParams});
            if (resp.data.status === 'error') {
                crud.setError(resp.data.message);
                return
            }
            console.log('loaded data',  resp.data.orders )
            crud.addItems(resp.data.orders);
            crud.clearLoading();
        } catch (e) {
            crud.setError(e);
        }
    }

    return {
        ...crud,
        makeOrder,
        checkPayment,
        cancelOrder,
        loadData,
        checkoutData,
        promo,
        applyPromo,
        clearPromo,
    }
});
